/**
 * Dynamic Checkout Loader
 * Hiển thị loading animation (3 dots) khi user click vào nút "Buy it now" (Dynamic Checkout Button)
 * 
 * @author MC-Project
 * @version 1.0.2
 */

class DynamicCheckoutLoader {
  constructor() {
    this.init();
  }
  
  /**
   * Khởi tạo loader cho tất cả Dynamic Checkout Buttons
   */
  init() {
    const wrappers = document.querySelectorAll('[data-payment-button-wrapper]');
    
    if (wrappers.length === 0) {
      this.observeForWrappers();
      return;
    }
    
    wrappers.forEach((wrapper) => {
      this.setupLoader(wrapper);
    });
  }
  
  /**
   * Observe DOM để detect khi wrapper được thêm vào
   */
  observeForWrappers() {
    const observer = new MutationObserver(() => {
      const wrapper = document.querySelector('[data-payment-button-wrapper]');
      if (wrapper) {
        observer.disconnect();
        this.setupLoader(wrapper);
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
  
  /**
   * Setup loader cho một wrapper cụ thể
   * @param {HTMLElement} wrapper - Wrapper element chứa Dynamic Checkout Button
   */
  setupLoader(wrapper) {
    const paymentButtons = wrapper.querySelectorAll('.shopify-payment-button__button');
    const loader = wrapper.querySelector('.button__loader--payment');
    
    if (!loader) return;
    
    if (paymentButtons.length === 0) {
      this.observeForButtons(wrapper, loader);
      return;
    }
    
    // Wrap button text vào span để có thể animate riêng (giống CustomButton)
    paymentButtons.forEach((button) => {
      this.wrapButtonText(button);
    });
    
    // Lắng nghe click vào TẤT CẢ các payment buttons (bao gồm BSS buttons)
    paymentButtons.forEach((button) => {
      button.addEventListener('click', () => {
        // ALWAYS show loader khi click, bất kể button state
        this.showLoader(wrapper, loader);
      });
    });
    
    // IMPORTANT: Observe cho BSS buttons được inject sau
    // BSS App thường inject button sau khi page load xong
    this.observeForBSSButtons(wrapper, loader);
    
    // Reset loader khi page load lại (browser back/forward)
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        this.hideLoader(wrapper, loader);
      }
    });
  }
  
  /**
   * Observe DOM để detect khi BSS B2B buttons được inject
   * @param {HTMLElement} wrapper - Wrapper element
   * @param {HTMLElement} loader - Loader element
   */
  observeForBSSButtons(wrapper, loader) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          // Check nếu node là button BSS
          if (node.nodeType === Node.ELEMENT_NODE && 
              node.classList && 
              (node.classList.contains('bss-b2b-btn-BuyItNow') || 
               node.classList.contains('bss-b2b-cp-dynamic'))) {
            
            console.log('✅ BSS button detected, attaching loader...');
            
            // Wrap text và attach event
            this.wrapButtonText(node);
            node.addEventListener('click', () => {
              this.showLoader(wrapper, loader);
            });
          }
          
          // Đệ quy check children
          if (node.querySelectorAll) {
            const bssButtons = node.querySelectorAll('.bss-b2b-btn-BuyItNow, .bss-b2b-cp-dynamic');
            bssButtons.forEach((button) => {
              console.log('✅ BSS button detected in children, attaching loader...');
              this.wrapButtonText(button);
              button.addEventListener('click', () => {
                this.showLoader(wrapper, loader);
              });
            });
          }
        });
      });
    });
    
    // Observe wrapper và toàn bộ subtree
    observer.observe(wrapper, {
      childList: true,
      subtree: true
    });
  }
  
  /**
   * Wrap button text vào span element để có thể animate riêng
   * Giống như CustomButton trong theme
   * @param {HTMLElement} button - Button element
   */
  wrapButtonText(button) {
    // Check nếu đã wrap rồi thì skip
    if (button.querySelector('.button__text')) return;
    
    // Lấy tất cả text nodes
    const textNodes = Array.from(button.childNodes).filter(
      node => node.nodeType === Node.TEXT_NODE && node.textContent.trim()
    );
    
    if (textNodes.length === 0) return;
    
    // Tạo span wrapper
    const textWrapper = document.createElement('span');
    textWrapper.className = 'button__text';
    
    // Move text vào span
    textNodes.forEach(node => {
      textWrapper.appendChild(node.cloneNode(true));
      node.remove();
    });
    
    // Insert span vào button
    button.insertBefore(textWrapper, button.firstChild);
  }
  
  /**
   * Observe DOM để detect khi payment buttons được render
   */
  observeForButtons(wrapper, loader) {
    const observer = new MutationObserver(() => {
      const buttons = wrapper.querySelectorAll('.shopify-payment-button__button');
      if (buttons.length > 0) {
        observer.disconnect();
        this.setupLoader(wrapper);
      }
    });
    
    observer.observe(wrapper, {
      childList: true,
      subtree: true
    });
  }
  
  /**
   * Hiển thị loader
   * @param {HTMLElement} wrapper - Wrapper element
   * @param {HTMLElement} loader - Loader element
   */
  showLoader(wrapper, loader) {
    wrapper.setAttribute('aria-busy', 'true');
    loader.removeAttribute('hidden');
  }
  
  /**
   * Ẩn loader
   * @param {HTMLElement} wrapper - Wrapper element
   * @param {HTMLElement} loader - Loader element
   */
  hideLoader(wrapper, loader) {
    wrapper.removeAttribute('aria-busy');
    loader.setAttribute('hidden', '');
  }
}

// Initialize khi DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new DynamicCheckoutLoader();
  });
} else {
  new DynamicCheckoutLoader();
}

// Re-initialize khi có section được reload (Shopify Theme Editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new DynamicCheckoutLoader();
  });
}
